/**
 * activityRegistry.js — ONE canonical activity identity over the existing catalogue.
 * Adapts lib/catalog.js (Everything) into: exactly one primaryType, secondaryTags, canonical aliases,
 * audience and child-facing labels. Nothing here duplicates content data — it classifies it.
 * Homepage discovery, Everything and duplicate audits read from this module.
 */
import { audienceOf, CATALOG } from "@/lib/catalog";

export const PRIMARY_TYPES = ["ACADEMY", "QUEST", "LESSON", "KNOWLEDGE", "GAME", "CHALLENGE", "STORY", "READING", "CREATIVE", "WORLD_DISCOVERY", "ASSESSMENT", "TOOL"];

const TYPE_MAP = { quest: "QUEST", learn: "LESSON", academy: "ACADEMY", assessment: "ASSESSMENT", game: "GAME", quiz: "GAME", puzzle: "GAME", story: "STORY", world: "WORLD_DISCOVERY", creative: "CREATIVE", challenge: "CHALLENGE", live: "CHALLENGE", tool: "TOOL", progress: "TOOL" };
const TAG_FROM_TYPE = { quiz: ["QUIZ"], puzzle: ["PUZZLE"], live: ["LIVE", "TOGETHER"], progress: ["PROGRESS"] };

// Identity decisions from the Jun 2026 duplicate/overlap audit (memory/DUPLICATE_IDENTITY_AUDIT.md).
// Labels live in lib/catalog.js (one source); this map only fixes type/tags/audience/identity.
const OVERRIDES = {
  // AI · one experience (/learn/ai). "AI Island" was the same route under another name.
  "feat-learn-ai": { primaryType: "QUEST", tags: ["ACADEMY", "AI", "DISCOVERY"] },
  // Code · one experience (/code-quest redirects to /learn/code-quest).
  "feat-code-quest": { primaryType: "QUEST", tags: ["ACADEMY", "CODE"] },
  "feat-times-tables-quest": { primaryType: "QUEST", tags: ["ACADEMY", "MATHS", "PRACTICE"] },
  // /treasure-quest is the unified Treasure Quest (Knowledge Hub merged into it, Feb 2026) — a QUEST, not a lesson list.
  "treasure-quest": { primaryType: "QUEST", tags: ["KNOWLEDGE", "PLAY"] },
  // English · /english-assessment is canonical; /language-quest/english-quest redirects to it.
  "english-assessment": { primaryType: "ASSESSMENT", tags: ["ENGLISH", "PROGRESS_CHECK"] },
  "feat-english-quest": { canonicalOf: "english-assessment" },
  "english-methodology": { primaryType: "TOOL", tags: ["HELP", "METHODOLOGY"] },
  "reading-placement": { primaryType: "ASSESSMENT", tags: ["ENGLISH", "READING", "STARTING_POINT"] },
  // Maths · /diagnostic/maths is a MATHS placement (generic "Skill check" title was mislabelled).
  diagnostic: { primaryType: "ASSESSMENT", tags: ["MATHS", "STARTING_POINT"] },
  placement: { primaryType: "ASSESSMENT", tags: ["MATHS", "STARTING_POINT"] },
  papers: { primaryType: "TOOL", tags: ["PRINT", "PRACTICE"] },
  // Reading & stories
  reading: { primaryType: "READING" }, "early-reading": { primaryType: "READING" }, "early-reading-course": { primaryType: "READING" },
  stories: { primaryType: "STORY" }, "story-arcs": { primaryType: "STORY", tags: ["FAMILY"] },
  // Knowledge to explore (curiosity/discovery)
  "animals-hub": { primaryType: "KNOWLEDGE" }, animals: { primaryType: "KNOWLEDGE" }, wonders: { primaryType: "KNOWLEDGE" }, cultures: { primaryType: "KNOWLEDGE" },
  nature: { primaryType: "KNOWLEDGE" }, discover: { primaryType: "ASSESSMENT", tags: ["STARTING_POINT"] }, "geo-lines": { primaryType: "KNOWLEDGE" }, values: { primaryType: "KNOWLEDGE" }, ask: { primaryType: "KNOWLEDGE" },
  // Adult tools surfaced in the catalogue (BETA) — not child activities.
  "teacher-console": { primaryType: "TOOL", tags: ["ADULT", "BETA"] },
  "homeschool-planner": { primaryType: "TOOL", tags: ["ADULT", "BETA"] },
  "tutor-console": { primaryType: "TOOL", tags: ["ADULT", "BETA"] },
  "learning-plan": { primaryType: "TOOL", tags: ["PLAN"] },
  // Hubs/launchers are discovery surfaces, not activities.
  today: { primaryType: "TOOL", tags: ["HUB"] }, "games-hub": { primaryType: "TOOL", tags: ["HUB", "GAME"] }, "play-launcher": { primaryType: "TOOL", tags: ["HUB", "GAME"] },
  "play-create": { primaryType: "TOOL", tags: ["HUB"] }, "learn-pathways": { primaryType: "TOOL", tags: ["HUB", "LEARN"] }, "learn-topics": { primaryType: "TOOL", tags: ["HUB", "LEARN"] },
  subjects: { primaryType: "TOOL", tags: ["HUB", "LEARN"] }, curriculum: { primaryType: "TOOL", tags: ["HUB", "LEARN"] }, "learning-hub": { primaryType: "TOOL", tags: ["HUB", "LEARN"] },
  lessons: { primaryType: "TOOL", tags: ["HUB", "LEARN"] }, "tools-hub": { primaryType: "TOOL", tags: ["HUB"] }, quiz: { primaryType: "TOOL", tags: ["HUB", "GAME"] },
  "create-hub": { primaryType: "TOOL", tags: ["HUB", "CREATIVE"] }, "maths-library": { primaryType: "TOOL", tags: ["HUB", "ACADEMY", "MATHS"] },
  focus: { primaryType: "LESSON", tags: ["FOCUS", "NEXT_ACTION"] },
  // Live family: ONE canonical Live Quest; solo / join-a-class / join-with-code / family are modes or entry paths, not products.
  live: { primaryType: "CHALLENGE", tags: ["LIVE", "LIVE_QUEST", "TOGETHER", "GROUP"] },
  "live-solo": { canonicalOf: "live", tags: ["LIVE", "MODE_SOLO"] },
  "live-daily": { primaryType: "CHALLENGE", tags: ["LIVE", "DAILY_CHALLENGE", "OPTIONAL"] },
  tournament: { primaryType: "CHALLENGE", tags: ["LIVE", "TOURNAMENT", "WEEKLY", "OPTIONAL"] },
  family: { primaryType: "TOOL", tags: ["HUB", "FAMILY", "LIVE_ENTRY"] }, "class-join": { primaryType: "TOOL", tags: ["JOIN", "LIVE_ENTRY"] }, "kid-join": { primaryType: "TOOL", tags: ["JOIN", "LIVE_ENTRY"] },
};

function classify(c) {
  const o = OVERRIDES[c.key] || {};
  const primaryType = o.primaryType || TYPE_MAP[c.type] || "TOOL";
  const tags = Array.from(new Set([...(TAG_FROM_TYPE[c.type] || []), ...(o.tags || [])]));
  return {
    activityId: c.key, canonicalId: o.canonicalOf || c.key, isAlias: !!o.canonicalOf,
    title: o.label || c.name, description: o.desc || c.desc, emoji: c.emoji, route: c.path,
    primaryType, secondaryTags: tags, subjects: o.subjects || c.subjects, ageTiers: c.tiers, minutes: c.minutes,
    audience: audienceOf(c) === "grown_up" ? "adult" : "child", catalogType: c.type, synonyms: c.synonyms, modeOf: c.modeOf || null, mode: c.mode || null,
  };
}

export const ACTIVITIES = CATALOG.map(classify);
export const ACTIVITY_BY_ID = Object.fromEntries(ACTIVITIES.map((a) => [a.activityId, a]));
export const activityFor = (id) => ACTIVITY_BY_ID[id] || null;

/** Child-visible canonical activities (no aliases, no adult tools, no hubs) for a tier and primary type or tag. */
export function activitiesFor({ primaryType, tag, tier } = {}) {
  return ACTIVITIES.filter((a) => !a.isAlias && a.audience === "child" && !a.secondaryTags.includes("HUB")
    && (!primaryType || a.primaryType === primaryType) && (!tag || a.secondaryTags.includes(tag)) && (!tier || a.ageTiers.includes(tier)));
}

/** Overlap report: same route, same canonical id, or near-identical titles — used by the identity audit. */
export function duplicateReport() {
  const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const out = [];
  for (let i = 0; i < ACTIVITIES.length; i++) for (let j = i + 1; j < ACTIVITIES.length; j++) {
    const a = ACTIVITIES[i], b = ACTIVITIES[j];
    const sameRoute = a.route === b.route, sameCanon = a.canonicalId === b.canonicalId, sameTitle = norm(a.title) === norm(b.title);
    if (sameRoute || sameCanon || sameTitle) out.push({ a: a.activityId, b: b.activityId, sameRoute, sameCanonical: sameCanon, sameTitle, classification: sameCanon ? "SHARED_EXPERIENCE" : sameRoute ? "EXACT_DUPLICATE" : "REQUIRES_REVIEW" });
  }
  return out;
}
